import Immutable from 'immutable';
import { createSelector } from 'reselect';
import {
  ROUTE_INSTITUTION_PAGE,
  ROUTE_USER_PROFILE_PAGE,
  ROUTE_OTHER_PAGE,
  ROUTE_COURSE_PAGE,
  ROUTE_BLOCKPAGE_CONTENT,
  ROUTE_BLOCK_WIDGET,
  BLOCKPAGE_WEBVIEW_SCREEN,
} from '../routeActions';
import { selectCurrentCoursePath } from '../Courses/selectors';
import {
  selectType,
  selectPayload,
  selectQuery,
  selectState,
} from '../location/selectors';

export const pagesSelector = (state) => state.Pages;

export const pagesByPath = createSelector(pagesSelector, (pages) =>
  pages.get('byPath')
);

export const makeSelectPageByPath = (path) =>
  createSelector([pagesByPath], (pages) => pages.get(path));

export const subpagesByPath = createSelector(pagesSelector, (pages) =>
  pages.get('subpagesByPath')
);

export const pagesByIdSelector = createSelector(pagesSelector, (pages) => {
  return pages
    .get('byPath')
    .reduce(
      (current, value) => current.set(value.get('id'), value),
      Immutable.Map()
    );
});

export const pagesByCoursePathSelector = createSelector(
  [pagesSelector],
  (pages) => {
    return pages.get('byCoursePath', Immutable.Map());
  }
);

export const makePagesSelectorByCoursePath = (coursePath) =>
  createSelector([pagesByCoursePathSelector], (pagesByCoursePath) => {
    return pagesByCoursePath.get(coursePath, Immutable.Map());
  });

// ================== WEB
export const currentCourseBlockPagesByPathSelector = createSelector(
  selectCurrentCoursePath,
  pagesSelector,
  (currentCourse, pages) => {
    return pages && pages.get('byPath')
      ? pages.get('byPath').filter((page) => {
          return (
            page.get('coursePath') === currentCourse &&
            page.get('mimetype') === undefined && // Filter out FilePages
            !page.get('isLegacyPage')
          );
        })
      : null;
  }
);

// WARNING: only works on the blockpage webview so far
export const selectCurrentPageRevisionId = createSelector(
  [selectType, selectQuery],
  (type, query) => {
    if (type === ROUTE_BLOCKPAGE_CONTENT) {
      return query.get('revision');
    }
    return null;
  }
);

// WARNING: only works on the blockpage webview so far
export const selectCurrentPageId = createSelector(
  [selectType, selectPayload],
  (type, params) => {
    if (type === ROUTE_BLOCKPAGE_CONTENT) {
      return params.get('pageId');
    }
    return null;
  }
);

// WARNING: only works on the widget webview so far
export const selectCurrentBlockId = createSelector(
  [selectType, selectPayload],
  (type, params) => {
    if (type === ROUTE_BLOCK_WIDGET) {
      return params.get('blockId');
    }
    return null;
  }
);

// WARNING: only works on the blockpage webview so far
export const selectShowComments = createSelector(
  [selectType, selectQuery],
  (type, query) => {
    if (type === ROUTE_BLOCKPAGE_CONTENT) {
      return query.get('comments') === 'true';
    }
    return false;
  }
);

// WARNING: only works on the blockpage webview so far
export const selectShowBottomNavigation = createSelector(
  [selectType, selectQuery],
  (type, query) => {
    if (type === ROUTE_BLOCKPAGE_CONTENT) {
      return query.get('bottomnavigation') === 'true';
    }
    return false;
  }
);

export const selectCurrentPagePath = createSelector(
  selectType,
  selectPayload,
  selectQuery,
  selectState,
  (type, params, query, state) => {
    switch (type) {
      // blockpage from notification/feed/course progress all belongs to course activity
      case BLOCKPAGE_WEBVIEW_SCREEN: {
        return query.get('pagePath');
      }
      case ROUTE_BLOCK_WIDGET:
      case ROUTE_BLOCKPAGE_CONTENT: {
        return query.get('path').toLowerCase();
      }
      case ROUTE_USER_PROFILE_PAGE: {
        return `u/${params.get('profileName')}${
          params.get('profileSubPath') ? `/${params.get('profileSubPath')}` : ''
        }`.toLowerCase();
      }
      case ROUTE_COURSE_PAGE: {
        const institutionPrefix = state.get('institutionPrefix');
        const pathWithoutPrefix = `courses/${params.get('courseCode')}${
          params.get('subPath') ? `/${params.get('subPath')}` : ''
        }`;
        if (institutionPrefix) {
          return `${institutionPrefix}/${pathWithoutPrefix}`.toLowerCase();
        } else {
          return pathWithoutPrefix.toLowerCase();
        }
      }
      case ROUTE_OTHER_PAGE: {
        return `${params.get('prefix')}${
          params.get('subPath') ? `/${params.get('subPath')}` : ''
        }`.toLowerCase();
      }
      case ROUTE_INSTITUTION_PAGE: {
        return `${state.get('institutionPrefix')}${
          params.get('subPath') ? `/${params.get('subPath')}` : ''
        }`.toLowerCase();
      }
      default:
        return null;
    }
  }
);

export const selectCurrentPage = createSelector(
  [selectCurrentPagePath, pagesByPath],
  (currentPagePath, pages) => {
    if (!currentPagePath) {
      return null;
    }
    return pages.get(currentPagePath);
  }
);

export const makeSelectSubpagesByPath = (pagePath) =>
  createSelector(subpagesByPath, pagesByIdSelector, (subpages, pagesById) => {
    const subpageIds = subpages.get(pagePath, Immutable.List());
    return subpageIds.map((id) => pagesById.get(id));
  });

export const selectSubPagesForSharingByCurrentCoursePath = createSelector(
  subpagesByPath,
  pagesByIdSelector,
  selectCurrentCoursePath,
  (subpages, pagesById, coursePath) => {
    const subpageIds = subpages.get(coursePath, Immutable.List());
    return subpageIds.map((id) => pagesById.get(id));
  }
);
